import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Branch } from '@/services/branch.service';

interface BranchState {
  // Current selected branch (can be main vendor or a branch)
  currentBranch: Branch | null;

  // All branches for the vendor
  branches: Branch[];

  // Main vendor (parent)
  mainVendor: Branch | null;

  // Loading state
  isLoading: boolean;

  // Actions
  setCurrentBranch: (branch: Branch | null) => void;
  setBranches: (branches: Branch[]) => void;
  setMainVendor: (vendor: Branch | null) => void;
  addBranch: (branch: Branch) => void;
  updateBranch: (branchId: string, updates: Partial<Branch>) => void;
  removeBranch: (branchId: string) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
}

const initialState = {
  currentBranch: null,
  branches: [],
  mainVendor: null,
  isLoading: false,
};

export const useBranchStore = create<BranchState>()(
  persist(
    (set) => ({
      ...initialState,

      setCurrentBranch: (branch) => set({ currentBranch: branch }),

      setBranches: (branches) => set({ branches }),

      setMainVendor: (vendor) => set({ mainVendor: vendor }),

      addBranch: (branch) =>
        set((state) => ({
          branches: [...state.branches, branch],
        })),

      updateBranch: (branchId, updates) =>
        set((state) => ({
          branches: state.branches.map((b) =>
            b.id === branchId ? { ...b, ...updates } : b
          ),
          currentBranch:
            state.currentBranch?.id === branchId
              ? { ...state.currentBranch, ...updates }
              : state.currentBranch,
        })),

      removeBranch: (branchId) =>
        set((state) => ({
          branches: state.branches.filter((b) => b.id !== branchId),
          currentBranch:
            state.currentBranch?.id === branchId ? null : state.currentBranch,
        })),

      setLoading: (loading) => set({ isLoading: loading }),

      reset: () => set(initialState),
    }),
    {
      name: 'branch-storage',
      partialize: (state) => ({
        currentBranch: state.currentBranch,
        branches: state.branches,
        mainVendor: state.mainVendor,
      }),
    }
  )
);
